import React from 'react'
import { Link } from '@inertiajs/react'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import type { BillingAlert } from '@/types/shared'

// THE GLOBAL BILLING STRIP (docs/plans/2026-08-20-billing-lifecycle-activation.md §6).
//
// ONE component, mounted by BOTH shells (OperatorLayout + PortfolioLayout) — the header-chrome
// convention. A per-layout fork is how two surfaces end up telling an operator two different
// things about the same unpaid invoice.
//
// It sits OUTSIDE the scrolling region in both shells, so an operator three screens down a
// calendar still sees it. It is deliberately NOT `position: fixed`: both shells already own a
// non-scrolling column head, and a fixed bar would sit on top of the sticky header / the sidebar
// instead of beside them.
//
// WHY THIS IS ALLOWED IN GLOBAL CHROME, unlike a target verdict: this is a FAULT — money is owed,
// the operator can resolve it, and acting on the CTA makes the strip go away. That is exactly the
// contract the fault rail carries. A target verdict re-derives every cycle and can never be
// "resolved", which is why it renders on the month it describes instead.
//
// EVERY SENTENCE IS SERVER-COMPOSED (AccountScoped#billing_alert_props). This component performs
// no date math, no money math, no pluralisation, no URL assembly — the same rule the frontend
// follows for rate data. `headline`, `detail` and `billingPath` are rendered verbatim; if a
// sentence needs to change, it changes in the composer.
//
// Non-dismissible on purpose: there is no "later" for an unpaid invoice, and a dismissed strip is
// indistinguishable from a healthy account.

interface Tone {
  /** Icon + surface for the state. Grace is a recoverable warning; blocked is a stopped account. */
  Icon: typeof AlertTriangle
  container: string
  icon: string
  headline: string
  detail: string
  invoice: string
  cta: string
}

const TONES: Record<BillingAlert['state'], Tone> = {
  // Amber — recoverable. Service is uninterrupted; the deadline in `detail` is the whole message.
  grace: {
    Icon: AlertTriangle,
    container: 'border-amber-200 border-l-amber-500 bg-amber-50',
    icon: 'text-amber-500',
    headline: 'text-amber-900',
    detail: 'text-amber-700',
    invoice: 'border-amber-200 bg-amber-100/70 text-amber-800',
    cta: 'bg-amber-500 text-white hover:bg-amber-600 focus-visible:outline-amber-600',
  },
  // Rose — stopped. The strongest tone in the system, matching the destructive semantic colour.
  blocked: {
    Icon: ShieldAlert,
    container: 'border-rose-200 border-l-rose-600 bg-rose-50',
    icon: 'text-rose-600',
    headline: 'text-rose-900',
    detail: 'text-rose-700',
    invoice: 'border-rose-200 bg-rose-100/70 text-rose-800',
    cta: 'bg-rose-600 text-white hover:bg-rose-700 focus-visible:outline-rose-700',
  },
}

export default function BillingAlertStrip({ alert }: { alert?: BillingAlert | null }) {
  // Null is the healthy / complimentary / un-billed account — every page sends the prop, so the
  // absent case is a render of nothing, never a fork at the call site.
  if (!alert) return null

  const tone = TONES[alert.state]
  if (!tone) return null
  const { Icon } = tone

  return (
    <div
      role="status"
      aria-live="polite"
      data-billing-alert={alert.state}
      className={`animate-slide-in border-b border-l-4 px-4 py-2.5 sm:px-6 lg:px-8 ${tone.container}`}
    >
      {/* One compact row on desktop; the CTA drops to its own line on narrow widths rather than
          squeezing the sentence that explains why it is there. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Icon className={`h-4 w-4 shrink-0 ${tone.icon}`} aria-hidden="true" />
        <p className={`text-sm font-medium ${tone.headline}`}>{alert.headline}</p>
        <p className={`text-xs ${tone.detail}`}>{alert.detail}</p>
        {alert.invoiceNumber && (
          <span
            className={`rounded-md border px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${tone.invoice}`}
          >
            <span className="sr-only">Invoice </span>
            {alert.invoiceNumber}
          </span>
        )}
        {/* Backend-served path to THIS account's Billing page — appended to by nobody. Null for a
            non-owner (billing is owner-only): the strip still informs, but a CTA into a door the
            viewer cannot open is not a CTA — the detail sentence reroutes the ask to an owner. */}
        {alert.billingPath && (
          <Link
            href={alert.billingPath}
            className={`ml-auto shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${tone.cta}`}
          >
            Resolve payment
          </Link>
        )}
      </div>
    </div>
  )
}
